// File: src/services/revenueService.ts

interface RevenueTransaction {
    id: number;
    appointment_id: number;
    service_id: number;
    total_amount: number;
    clinic_amount: number;
    provider_amount: number;
    owner_amount: number;
    referrer_amount: number;
    charity_amount: number;
    referrer_name?: string;
    transaction_date: string;
    appointment?: any;
    service?: any;
}
interface OwnerEarnings {
    owner_id: number;
    owner_name: string;
    owner_role: string;
    total_earned: number;
    transactions_count: number;
    avg_per_transaction: number;
    equipment_used: {
        equipment_id: number;
        equipment_name: string;
        equipment_type: string;
        usage_count: number;
        earned: number;
        avg_per_use: number;
    }[];
    services_used: {
        service_name: string;
        count: number;
        earned: number;
    }[];
    recent_usages: {
        date: string;
        patient_name: string;
        service: string;
        equipment: string;
        earned: number;
    }[];
}
interface ReferrerEarnings {
    referrer_id: number | null;
    referrer_name: string;
    total_earned: number;
    transactions_count: number;
    avg_per_referral: number;
    services_referred: {
        service_name: string;
        count: number;
        earned: number;
    }[];
    recent_referrals: {
        date: string;
        patient_name: string;
        service: string;
        earned: number;
    }[];
}
interface RevenueSummary {
    total_transactions: number;
    total_revenue: number;
    clinic_revenue: number;
    provider_revenue: number;
    owner_revenue: number;
    referrer_revenue: number;
    charity_revenue: number;
    avg_transaction_value: number;
}

interface RevenueKPIs {
    current_period: RevenueSummary;
    previous_period: RevenueSummary;
    growth_metrics: {
        revenue_growth_percentage: number;
        transaction_growth_percentage: number;
    };
    popular_service?: {
        name: string;
        usage_count: number;
    };
    period_info: {
        start_date: string;
        end_date: string;
        days_count: number;
    };
}

interface DailyAnalytics {
    transaction_date: string;
    transactions_count: number;
    total_revenue: number;
    clinic_revenue: number;
    provider_revenue: number;
    charity_revenue: number;
    avg_transaction_value: number;
}

interface ProviderPerformance {
    provider_id: number;
    provider_name: string;
    provider_role: string;
    transactions_count: number;
    total_revenue: number;
    provider_earnings: number;
    avg_transaction_value: number;
}

interface ServicePopularity {
    service_id: number;
    service_name: string;
    service_category: string;
    usage_count: number;
    total_revenue: number;
    avg_fee: number;
    provider_name: string;
}

// Doctor Earnings Interface
interface DoctorEarnings {
    doctor_id: number;
    doctor_name: string;
    specialization: string;
    total_earned: number;
    transactions_count: number;
    avg_per_transaction: number;
    services_provided: {
        name: string;
        count: number;
        earned: number;
    }[];
    growth_percentage: number;
    pending_amount: number;
    last_payment_date: string | null;
    payment_status: 'paid' | 'pending' | 'overdue';
}

// Doctor Payment Interface
interface DoctorPayment {
    doctor_id: number;
    amount: number;
    payment_date: string;
    payment_method: string;
    payment_reference?: string;
    notes?: string;
}

class RevenueService {
    private static baseURL = process.env.REACT_APP_API_URL || 'https://dr-bassam-elayoubi.caduceuslb.com/api';

    
    private static async request(url: string, options: RequestInit = {}): Promise<any> {
    const token = localStorage.getItem('auth_token');
    
    const config: RequestInit = {
        headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
            ...options.headers,
        },
        ...options,
    };

    const response = await fetch(`${this.baseURL}${url}`, config);

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle role-based access errors
        if (response.status === 403) {
            throw new Error(errorData.message || 'Access denied. You do not have permission to view this data.');
        }
        
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
}
    // =============== CORE REVENUE METHODS ===============
    static async getOwnerEarnings(params: {
        start_date?: string;
        end_date?: string;
        owner_id?: number;
    } = {}): Promise<OwnerEarnings[]> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, value.toString());
            }
        });

        return this.request(`/revenue/owner-earnings?${queryParams}`);
    }

    static async getOwnerEarningsById(ownerId: number, params: {
        start_date?: string;
        end_date?: string;
    } = {}): Promise<OwnerEarnings> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, value.toString());
            }
        });

        return this.request(`/revenue/owner-earnings/${ownerId}?${queryParams}`);
    }
    static async getSummary(params: {
        start_date?: string;
        end_date?: string;
        provider_id?: number;
    } = {}): Promise<RevenueSummary> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, value.toString());
            }
        });

        return this.request(`/revenue/summary?${queryParams}`);
    }
    static async getReferrerEarnings(params: {
        start_date?: string;
        end_date?: string;
        referrer_id?: number;
    } = {}): Promise<ReferrerEarnings[]> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, value.toString());
            }
        });

        return this.request(`/revenue/referrer-earnings?${queryParams}`);
    }

    static async getReferrerEarningsById(referrerId: number, params: {
        start_date?: string;
        end_date?: string;
    } = {}): Promise<ReferrerEarnings> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, value.toString());
            }
        });

        return this.request(`/revenue/referrer-earnings/${referrerId}?${queryParams}`);
    }
    static async getTransactions(params: {
        start_date?: string;
        end_date?: string;
        service_id?: number;
        per_page?: number;
    } = {}): Promise<{ data: RevenueTransaction[]; meta?: any }> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, value.toString());
            }
        });

        return this.request(`/revenue/transactions?${queryParams}`);
    }

    // =============== ANALYTICS METHODS ===============

    static async getDailyAnalytics(days: number = 30): Promise<DailyAnalytics[]> {
        return this.request(`/revenue/analytics/daily?days=${days}`);
    }

    static async getMonthlyAnalytics(months: number = 12): Promise<any[]> {
        return this.request(`/revenue/analytics/monthly?months=${months}`);
    }

    static async getProviderPerformance(params: {
        start_date?: string;
        end_date?: string;
    } = {}): Promise<ProviderPerformance[]> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, value.toString());
            }
        });

        return this.request(`/revenue/analytics/provider-performance?${queryParams}`);
    }

    static async getServicePopularity(params: {
        start_date?: string;
        end_date?: string;
    } = {}): Promise<ServicePopularity[]> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, value.toString());
            }
        });

        return this.request(`/revenue/analytics/service-popularity?${queryParams}`);
    }

    static async getEquipmentUtilization(params: {
        start_date?: string;
        end_date?: string;
    } = {}): Promise<any[]> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, value.toString());
            }
        });

        return this.request(`/revenue/analytics/equipment-utilization?${queryParams}`);
    }

    static async getRevenueKPIs(params: {
        start_date?: string;
        end_date?: string;
    } = {}): Promise<RevenueKPIs> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, value.toString());
            }
        });

        return this.request(`/revenue/analytics/kpis?${queryParams}`);
    }

    // =============== DOCTOR EARNINGS METHODS ===============

    static async getDoctorEarnings(params: {
        start_date?: string;
        end_date?: string;
        doctor_id?: number;
        include_pending?: boolean;
    } = {}): Promise<DoctorEarnings[]> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, value.toString());
            }
        });

        return this.request(`/revenue/doctor-earnings?${queryParams}`);
    }

    static async getDoctorEarningsById(doctorId: number, params: {
        start_date?: string;
        end_date?: string;
    } = {}): Promise<DoctorEarnings> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, value.toString());
            }
        });

        return this.request(`/revenue/doctor-earnings/${doctorId}?${queryParams}`);
    }

    static async getDoctorPaymentHistory(doctorId: number): Promise<DoctorPayment[]> {
        return this.request(`/revenue/doctor-payments/${doctorId}/history`);
    }

    static async processDoctorPayment(payment: DoctorPayment): Promise<{ 
        success: boolean; 
        payment_id: number;
        message: string;
    }> {
        return this.request('/revenue/doctor-payments', {
            method: 'POST',
            body: JSON.stringify(payment),
        });
    }

    static async bulkProcessDoctorPayments(payments: DoctorPayment[]): Promise<{ 
        success: boolean; 
        processed: number; 
        failed: number; 
        results: any[] 
    }> {
        return this.request('/revenue/doctor-payments/bulk', {
            method: 'POST',
            body: JSON.stringify({ payments }),
        });
    }

    static async markDoctorPaymentAsPaid(doctorId: number, amount: number, paymentData: {
        payment_method: string;
        payment_reference?: string;
        notes?: string;
    }): Promise<{ success: boolean; payment_id: number }> {
        return this.request(`/revenue/doctor-payments/${doctorId}/mark-paid`, {
            method: 'POST',
            body: JSON.stringify({ amount, ...paymentData }),
        });
    }

    // =============== REPORTS ===============

    static async getDoctorEarningsReport(params: {
        start_date: string;
        end_date: string;
        format?: 'json' | 'csv' | 'pdf';
        doctor_ids?: number[];
    }): Promise<any> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                if (Array.isArray(value)) {
                    value.forEach(item => queryParams.append(`${key}[]`, item.toString()));
                } else {
                    queryParams.append(key, value.toString());
                }
            }
        });

        return this.request(`/revenue/reports/doctor-earnings?${queryParams}`);
    }

    // =============== HELPER METHODS ===============

    static async getByAppointment(appointmentId: number): Promise<{
        total_revenue: number;
        transactions: RevenueTransaction[];
        breakdown: {
            clinic_total: number;
            provider_total: number;
            owner_total: number;
            referrer_total: number;
            charity_total: number;
        };
    }> {
        return this.request(`/revenue/by-appointment/${appointmentId}`);
    }

    static async getByProvider(providerId: number): Promise<RevenueTransaction[]> {
        return this.request(`/revenue/by-provider/${providerId}`);
    }

    static async getByService(serviceId: number): Promise<RevenueTransaction[]> {
        return this.request(`/revenue/by-service/${serviceId}`);
    }

    static async getByDateRange(startDate: string, endDate: string): Promise<RevenueTransaction[]> {
        return this.request(`/revenue/by-date-range?start_date=${startDate}&end_date=${endDate}`);
    }

    // =============== UTILITY METHODS ===============

    static calculateGrowthPercentage(current: number, previous: number): number {
        if (previous === 0) return current > 0 ? 100 : 0;
        return ((current - previous) / previous) * 100;
    }

    static formatCurrency(amount: number): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount || 0);
    }

    static formatPercentage(value: number): string {
        return `${value >= 0 ? '+' : ''}${(value || 0).toFixed(1)}%`;
    }

    static getGrowthColor(percentage: number): string {
        if (percentage > 0) return '#10b981'; // Green
        if (percentage < 0) return '#ef4444'; // Red
        return '#6b7280'; // Gray
    }

    // =============== DOCTOR EARNINGS UTILITY METHODS ===============

    static calculateDoctorEarningsGrowth(current: DoctorEarnings[], previous: DoctorEarnings[]): DoctorEarnings[] {
        return current.map(currentDoctor => {
            const previousDoctor = previous.find(p => p.doctor_id === currentDoctor.doctor_id);
            const growthPercentage = previousDoctor 
                ? this.calculateGrowthPercentage(currentDoctor.total_earned, previousDoctor.total_earned)
                : 0;
            
            return {
                ...currentDoctor,
                growth_percentage: growthPercentage
            };
        });
    }

    static getTotalPendingPayments(doctorEarnings: DoctorEarnings[]): number {
        return doctorEarnings.reduce((total, doctor) => total + doctor.pending_amount, 0);
    }

    static getDoctorsWithPendingPayments(doctorEarnings: DoctorEarnings[]): DoctorEarnings[] {
        return doctorEarnings.filter(doctor => doctor.pending_amount > 0);
    }

    static getTopEarningDoctors(doctorEarnings: DoctorEarnings[], limit: number = 5): DoctorEarnings[] {
        return [...doctorEarnings]
            .sort((a, b) => b.total_earned - a.total_earned)
            .slice(0, limit);
    }

    // =============== PAYMENT STATUS HELPERS ===============

    static getPaymentStatusColor(status: 'paid' | 'pending' | 'overdue'): string {
        switch (status) {
            case 'paid': return '#10b981'; // Green
            case 'pending': return '#f59e0b'; // Yellow
            case 'overdue': return '#ef4444'; // Red
            default: return '#6b7280'; // Gray
        }
    }

    static getPaymentStatusText(status: 'paid' | 'pending' | 'overdue'): string {
        switch (status) {
            case 'paid': return '✅ Paid';
            case 'pending': return '⏳ Pending';
            case 'overdue': return '❌ Overdue';
            default: return '❓ Unknown';
        }
    }

    // =============== FINANCIAL CALCULATIONS ===============

    static calculateTotalRevenue(transactions: RevenueTransaction[]): number {
        return transactions.reduce((total, transaction) => total + transaction.total_amount, 0);
    }

    static calculateRevenueByType(transactions: RevenueTransaction[]): {
        clinic: number;
        provider: number;
        owner: number;
        referrer: number;
        charity: number;
    } {
        return transactions.reduce((totals, transaction) => ({
            clinic: totals.clinic + transaction.clinic_amount,
            provider: totals.provider + transaction.provider_amount,
            owner: totals.owner + transaction.owner_amount,
            referrer: totals.referrer + transaction.referrer_amount,
            charity: totals.charity + transaction.charity_amount,
        }), {
            clinic: 0,
            provider: 0,
            owner: 0,
            referrer: 0,
            charity: 0,
        });
    }

    static getRevenuePercentages(transactions: RevenueTransaction[]): {
        clinic: number;
        provider: number;
        owner: number;
        referrer: number;
        charity: number;
    } {
        const totals = this.calculateRevenueByType(transactions);
        const totalRevenue = this.calculateTotalRevenue(transactions);
        
        if (totalRevenue === 0) {
            return { clinic: 0, provider: 0, owner: 0, referrer: 0, charity: 0 };
        }

        return {
            clinic: (totals.clinic / totalRevenue) * 100,
            provider: (totals.provider / totalRevenue) * 100,
            owner: (totals.owner / totalRevenue) * 100,
            referrer: (totals.referrer / totalRevenue) * 100,
            charity: (totals.charity / totalRevenue) * 100,
        };
    }

    // =============== DATE HELPERS ===============

    static formatDate(date: string | Date): string {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }

    static getDateRange(days: number): { start_date: string; end_date: string } {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);

        return {
            start_date: startDate.toISOString().split('T')[0],
            end_date: endDate.toISOString().split('T')[0]
        };
    }

    static getPreviousPeriod(startDate: string, endDate: string): { start_date: string; end_date: string } {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffInDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

        const prevEnd = new Date(start);
        prevEnd.setDate(prevEnd.getDate() - 1);
        
        const prevStart = new Date(prevEnd);
        prevStart.setDate(prevStart.getDate() - diffInDays);

        return {
            start_date: prevStart.toISOString().split('T')[0],
            end_date: prevEnd.toISOString().split('T')[0]
        };
    }
}

export default RevenueService;
export type {
    RevenueTransaction,
    RevenueSummary,
    RevenueKPIs,
    DailyAnalytics,
    ProviderPerformance,
    ServicePopularity,
    DoctorEarnings,
    DoctorPayment,
    ReferrerEarnings,
    OwnerEarnings,
};